<template>
	<section class="Miniapp">
		<div class="item" v-for="item in items" :data-item="item.type" :key="`item_${item.id}`">
			<img v-if="item.type === 'image'" :src="parseJSON(item.items)" />
			<img v-else-if="item.type === 'qrcode'" :src="getQRCode(item.uri)" />
			<Form
				v-else-if="['input', 'textarea', 'tabmenu', 'yymmdd', 'checkbox', 'radio', 'dropdown', 'switch', 'file', 'time'].includes(item.type)"
				:form="getForm(item)"
				:data="getFormData(item)"
				@change="onFormChange"
			/>
			<div v-else-if="'calendar' === item.type">
				<h3 class="title">{{ item.title }}</h3>
				<Calendar v-bind="getCalendarData(item)" @select="onSelectDate(item, $event)" />
			</div>
			<div v-else-if="item.type === 'buttons'" class="buttons">
				<Button
					v-for="({ type, title }, i) in parseJSON(item.items)"
					:disabled="!(type !== 'submit' || fulfilled)"
					:key="`button_${i}`"
					:data-type="type"
					@click="onClickButton(item, i)"
					>{{ title }}</Button
				>
			</div>
			<input type="hidden" v-else-if="item.type === 'hidden'" :name="item.name" :value="item.state_key || ''" />
			<div v-else-if="item.type === 'html'" class="html" v-html="getHTML(item.desc)"></div>
			<hr v-else-if="item.type === 'divider'" />
			<element v-else-if="item.type === 'text'" :is="item.subtype || 'p'" :style="item.css">{{ item.desc }}</element>
			<span v-else>{{ item.type }}: {{ item.title }}</span>
		</div>
	</section>
</template>

<script>
	import Calendar from './Calendar';

	export default {
		props: ['miniapp'],
		components: {
			Calendar
		},

		data() {
			return {
				data: Object.values(this.parseJSON(this.miniapp.items) || {}).reduce(
					(acc, cur) => ({
						...acc,
						[cur.id]: cur.default || ''
					}),
					{}
				)
			};
		},

		methods: {
			onDataChange(key, value) {
				// this.data[key] = value;
				this.data = { ...this.data, [key]: value };
			},

			onFormChange(key, value) {
				this.onDataChange(key, value);
			},

			getForm(item) {
				const form = {
					label: item.title,
					name: item.id,
					type:
						{
							input: 'text',
							textarea: 'text',
							tabmenu: 'radio',
							yymmdd: 'date',
							checkbox: 'radio',
							radio: 'radio',
							dropdown: 'dropdown',
							switch: 'toggle',
							file: 'image',
							time: 'time'
						}[item.type] || 'text'
				};

				if (![undefined, 0, '0', '', false, null].includes(item.required)) {
					form.required = true;
				}

				if ('textarea' === item.type) {
					form.multiline = true;
				} else if ('time' === item.time) {
					form.step = item.opts?.step / 60 || 1;
				} else if (['checkbox', 'tabmenu', 'radio', 'dropdown'].includes(item.type)) {
					if (typeof this.parseJSON(item.items) === 'string') {
						form.options = item.items.split('\n').map(text => ({
							value: text,
							label: text
						}));
					} else {
						form.options = this.parseJSON(item.items).map(text => ({
							value: text,
							label: text
						}));
					}
					if (['checkbox'].includes(item.type)) {
						form.multiselect = true;
					} else {
						form.multiselect = false;
					}
				}

				return [form];
			},

			getFormData(item) {
				return {
					[item.id]: this.data[item.id] || ''
				};
			},

			getCalendarData(item) {
				const config = {
					instant: true,
					type: item.subtype || 'date'
				};

				if (item?.opts?.limitation === 'date') {
					config.start = new Date(item.opts.start_date);
					config.period = (new Date(item.opts.end_date).getTime() - new Date(item.opts.start_date).getTime()) / (24 * 60 * 60 * 1000);
				}

				return config;
			},

			getQRCode(text) {
				return `https://chart.googleapis.com/chart?cht=qr&chs=256x256&chl=${text}&choe=UTF-8`;
			},

			getHTML(text) {
				const decodeHTML = function(html) {
					const txt = document.createElement('textarea');
					txt.innerHTML = html;
					return txt.value;
				};

				return decodeHTML(text);
			},

			onSelectDate(item, data) {
				let date = data[0].toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' });
				if (data[1]) {
					date += ` ${data[1].toString().padStart(2, 0)}:00:00`;
				}
				this.onDataChange(item.id, date);
			},

			onClickButton(button, index) {
				if (this.parseJSON(button.items)[index].type === 'submit') {
					this.$emit('submit', this.assembledData, button, this.parseJSON(button.items)[index]);
				}
			}
		},

		computed: {
			items() {
				return this.miniapp.cids.map(cid => this.miniapp.items[cid]);
			},

			fulfilled() {
				return Object.entries(this.miniapp.items).every(([k, v]) => {
					if ([undefined, 0, '0', '', false, null].includes(v.required)) {
						return true;
					}

					return !([undefined, '', null].includes(this.data[k]) || this.data[k].length === 0);
				});
			},

			assembledData() {
				return Object.fromEntries(
					Object.entries(this.data)
						.map(([k, v]) => [this.miniapp.items[k].name, Array.isArray(v) ? v.join('\n') : v])
						.filter(([k]) => k)
				);
			}
		},

		beforeMount() {
			this.logX('MINIAPP', '#c93', this.miniapp);
			Object.values(this.miniapp.items).forEach(item => {
				if (item.default) {
					if (item.type === 'checkbox') {
						this.data = { ...this.data, [item.id]: item.default.split('\n') };
					} else {
						this.data = { ...this.data, [item.id]: item.default };
					}
				}
			});
		}
	};
</script>

<style lang="scss" scoped>
	.Miniapp {
		padding: 1rem 1.6rem;
		position: relative;

		.title {
			font-size: 1.2rem;
			opacity: 0.6;
			font-weight: normal;
			margin-bottom: 0.5rem;
		}

		.item {
			position: relative;

			&:not(:first-child) {
				margin-top: 2rem;
			}

			&[data-item='image'],
			&[data-item='qrcode'] {
				// margin: -1rem -1.6rem;
				margin-bottom: 0;
				--height: initial;

				img {
					width: 100%;
					height: var(--height);
					object-fit: cover;
				}
			}

			/deep/ .Form {
				.title {
					font-size: 1.2rem;
					opacity: 0.6;
				}
			}

			&[data-item='divider'] {
				opacity: 0.5;
			}

			&[data-item='buttons'] {
				.buttons {
					.Button {
						&:not(:first-child) {
							margin-top: 1rem;
						}
						width: 100%;
					}
				}
			}

			&[data-item='calendar'] {
				/deep/ .Calendar {
					border-radius: 0.5rem;
					overflow: hidden;
					background-color: #eee;
					// max-width: 48rem;
				}
			}

			&[data-item='tabmenu'] {
				/deep/ .Form {
					.input-area {
						display: flex;
						flex-direction: row;
						margin: 0;

						.checkbox {
							flex: 1;
							text-align: center;
							border: solid 1px rgba(0, 0, 0, 0.15);
							margin: 0;
							padding: 0.75rem 0;

							&:first-of-type {
								border-top-left-radius: 0.75rem;
								border-bottom-left-radius: 0.75rem;
							}

							&:last-of-type {
								border-top-right-radius: 0.75rem;
								border-bottom-right-radius: 0.75rem;
							}

							&:not(:first-of-type) {
								margin-left: -1px;
							}

							&::before {
								display: none;
							}

							&.checked {
								background-color: var(--clrTheme);
								span {
									color: white;
								}
							}
						}
					}
				}
			}
		}
	}
</style>
